import { useState } from 'react';
import { AssessedPoint, BehaviourDetail, Distribution } from './types';
import { dateTime, number } from './helpers';

export const baselineLabels = {
  manual: 'Angivet forventning',
  operating_history: 'Sammenlignelig drift',
  weekday_history: 'Tilsvarende ugedage',
  recent_history: 'Seneste historik',
  flow_threshold: 'Niveaugrænse',
};
const stateLabels = { operating: 'I brug', idle: 'Ikke i brug', unknown: 'Ukendt drift' };
const weekLabels = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'];
const value = (n: number | null) => (n === null ? '—' : number(n));
function DistributionCards({ data, unit }: { data: Distribution; unit: string }) {
  return (
    <div className="behaviour-stat-cards">
      <div>
        <span>Typisk (median)</span>
        <strong>
          {value(data.median)} <small>{unit}</small>
        </strong>
      </div>
      <div>
        <span>Gennemsnit</span>
        <strong>
          {value(data.mean)} <small>{unit}</small>
        </strong>
      </div>
      <div>
        <span>Midterste 80 % af målingerne</span>
        <strong>
          {value(data.p10)}–{value(data.p90)}
        </strong>
      </div>
      <div>
        <span>Gyldige måleperioder</span>
        <strong>{data.count}</strong>
      </div>
    </div>
  );
}
function SeriesChart({
  points,
  unit,
  included = true,
}: {
  points: AssessedPoint[];
  unit: string;
  included?: boolean;
}) {
  if (!points.length)
    return <p className="anomaly-muted">Ingen gyldige måleperioder i dette udsnit.</p>;
  const width = 800,
    height = 230,
    left = 65,
    right = 20,
    top = 18,
    bottom = 32;
  const start = points[0].at,
    end = points[points.length - 1].at;
  const high = Math.max(1, ...points.flatMap((p) => [p.value, p.high ?? 0])) * 1.1;
  const x = (at: number) =>
    left + ((at - start) / Math.max(1, end - start)) * (width - left - right);
  const y = (n: number) => height - bottom - (n / high) * (height - top - bottom);
  return (
    <figure className="behaviour-series">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={
          unit === 'cm'
            ? 'Målt niveau i cm'
            : !included
            ? `Målt forbrug i ${unit}, ikke valgt til analyse`
            : `Målt forbrug og forventet interval i ${unit}`
        }
      >
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={left} x2={width - right} y1={y(high * f)} y2={y(high * f)} stroke="#e2e8f0" />
            <text x={left - 8} y={y(high * f) + 4} textAnchor="end" fontSize="11" fill="#64748b">
              {value(high * f)}
            </text>
          </g>
        ))}
        {points.slice(1).map((p, i) => {
          const previous = points[i];
          if (p.at - previous.at > 36 * 3600000) return null;
          return (
            <g key={p.at}>
              {p.low !== null &&
                p.high !== null &&
                previous.low !== null &&
                previous.high !== null && (
                  <polygon
                    points={`${x(previous.at)},${y(previous.high)} ${x(p.at)},${y(p.high)} ${x(
                      p.at
                    )},${y(p.low)} ${x(previous.at)},${y(previous.low)}`}
                    fill="#dbeafe"
                  />
                )}
              <line
                x1={x(previous.at)}
                y1={y(previous.value)}
                x2={x(p.at)}
                y2={y(p.value)}
                stroke="#0284c7"
                strokeWidth="2"
              />
            </g>
          );
        })}
        {points.map((p) => (
          <circle
            key={p.at}
            cx={x(p.at)}
            cy={y(p.value)}
            r={p.outside ? 5 : 3}
            fill={p.outside ? '#d97706' : '#0284c7'}
          >
            <title>
              {dateTime(p.at)} · {value(p.value)} {unit} ·{' '}
              {stateLabels[p.assessment.operatingState]} ·{' '}
              {!included
                ? 'Ikke valgt til analyse'
                : unit === 'cm'
                ? 'Niveaumåling'
                : p.outside === null
                ? 'Ikke nok grundlag'
                : `${value(p.low)}–${value(p.high)} forventet`}
            </title>
          </circle>
        ))}
      </svg>
      <figcaption>
        {dateTime(start)} — {dateTime(end)} · {unit}
      </figcaption>
      <p className="anomaly-muted">
        {!included
          ? 'Blå: målt forbrug. Ikke valgt til analyse.'
          : unit === 'cm'
          ? 'Blå: målt niveau. Store huller forbindes ikke.'
          : 'Blå: målt · lys blå: beregnet interval · orange: uden for intervallet. Store huller forbindes ikke.'}
      </p>
    </figure>
  );
}
export default function Statistics({
  detail,
  preview,
}: {
  detail: BehaviourDetail;
  preview: boolean;
}) {
  const [metric, setMetric] = useState<'water' | 'binder'>('water');
  const [state, setState] = useState('all');
  const [onlyOutside, setOnlyOutside] = useState(false);
  if (!detail.dataAt)
    return (
      <div className="anomaly-empty">
        <h3>Statistik afventer næste analyse</h3>
        <p>Du kan allerede definere normal adfærd under Indstillinger.</p>
      </div>
    );
  if (detail.plant.deviceFamily === 'FLOW') {
    const flow = detail.flow;
    if (!flow?.count) return <p>Ingen gyldige niveaumålinger i de seneste 7 dage.</p>;
    return (
      <section>
        <p className="anomaly-muted">
          Niveau · seneste 7 dage · opdateret {dateTime(detail.dataAt)}
        </p>
        <DistributionCards data={flow} unit="cm" />
        <p>
          <strong>{flow.jumpCount}</strong> niveauhændelser krydsede springgrænsen ved seneste
          analyse.
        </p>
        <p className="anomaly-muted">
          Tællingen bruger den seneste analyses grænser og samler nærliggende spring. Ændrede
          grænser gælder ved næste analyse. Kurven viser op til 200 udvalgte målinger; statistikken
          bruger alle {flow.count}.
        </p>
        <SeriesChart
          unit="cm"
          points={flow.points.map((p) => ({
            ...p,
            low: null,
            high: null,
            outside: null,
            assessment: {
              profileVersion: 0,
              operatingState: 'unknown',
              contextSource: 'unknown',
              baselineSource: 'flow_threshold',
              historyCount: 0,
              fallback: false,
            },
          }))}
        />
        <details>
          <summary>Vis kurvens målinger</summary>
          <div className="behaviour-table-wrap">
            <table className="behaviour-table">
              <thead>
                <tr>
                  <th>Tidspunkt</th>
                  <th>Niveau (cm)</th>
                </tr>
              </thead>
              <tbody>
                {flow.points.map((p) => (
                  <tr key={p.at}>
                    <td>{dateTime(p.at)}</td>
                    <td>{value(p.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      </section>
    );
  }
  const stats = detail.usage.find((s) => s.metric === metric);
  if (!stats) return <p>Forbrugsstatistik afventer målinger.</p>;
  const included = stats.included !== false;
  const points = stats.points.filter(
    (p) =>
      (state === 'all' || p.assessment.operatingState === state) &&
      (!included || !onlyOutside || p.outside)
  );
  return (
    <section>
      <div className="behaviour-tabs" aria-label="Forbrugstype">
        <button type="button" aria-pressed={metric === 'water'} onClick={() => setMetric('water')}>
          Vand
        </button>
        <button
          type="button"
          aria-pressed={metric === 'binder'}
          onClick={() => setMetric('binder')}
        >
          Binder
        </button>
      </div>
      <p className="anomaly-muted">
        Seneste 28 dage · opdateret {dateTime(detail.dataAt)}.{' '}
        {preview
          ? 'Forhåndsvisning af ugemte indstillinger.'
          : 'Beregnet med de gemte indstillinger.'}
      </p>
      <DistributionCards data={stats.distribution} unit={stats.unit} />
      {!included && (
        <p className="anomaly-warning">
          {metric === 'water' ? 'Vand' : 'Binder'} er ikke valgt til analyse. Viser kun rå
          statistik; disse målinger vurderes ikke og skaber ikke nye afvigelser.
        </p>
      )}
      {included && (
        <div className="behaviour-findings">
          <strong>
            {stats.outsideCount} af {stats.assessedCount} vurderbare perioder uden for intervallet
            {stats.outsidePercent === null ? '' : ` (${value(stats.outsidePercent)} %)`}
          </strong>
          <p>
            {stats.distribution.count - stats.assessedCount} perioder mangler grundlag for en
            vurdering. Procentsatsen omfatter kun vurderbare perioder.
          </p>
          <p>
            Historisk beregning — dette er mulige afvigelser, ikke bekræftede fejl. Gemte hændelser
            i køen ændres ikke.
          </p>
        </div>
      )}
      <div className="behaviour-state-grid">
        {(['operating', 'idle', 'unknown'] as const).map((key) => (
          <div key={key}>
            <strong>{stateLabels[key]}</strong>
            <span>
              {stats[key].count} perioder · median {value(stats[key].median)} l/døgn
            </span>
          </div>
        ))}
      </div>
      <SeriesChart points={stats.points} unit={stats.unit} included={included} />
      <h3>Forbrug fordelt på ugedage</h3>
      <div className="behaviour-weekdays">
        {stats.weekdays.map((d) => (
          <div key={d.day}>
            <span>{weekLabels[d.day]}</span>
            <meter
              aria-label={`${weekLabels[d.day]} medianforbrug`}
              min={0}
              max={Math.max(1, ...stats.weekdays.map((w) => w.distribution.median ?? 0))}
              value={d.distribution.median ?? 0}
            />
            <strong>{value(d.distribution.median)}</strong>
            <small>{d.distribution.count} perioder</small>
          </div>
        ))}
      </div>
      <div className="behaviour-table-filters">
        <h3>Målinger og forklaringer</h3>
        <select
          aria-label="Filtrér driftstilstand"
          value={state}
          onChange={(e) => setState(e.target.value)}
        >
          <option value="all">Alle driftsperioder</option>
          <option value="operating">I brug</option>
          <option value="idle">Ikke i brug</option>
          <option value="unknown">Ukendt drift</option>
        </select>
        <label>
          <input
            type="checkbox"
            disabled={!included}
            checked={included && onlyOutside}
            onChange={(e) => setOnlyOutside(e.target.checked)}
          />{' '}
          Kun uden for intervallet
        </label>
      </div>
      <div className="behaviour-table-wrap">
        <table className="behaviour-table">
          <thead>
            <tr>
              <th>Tidspunkt</th>
              <th>Målt l/døgn</th>
              <th>Forventet</th>
              <th>Drift</th>
              <th>Grundlag</th>
            </tr>
          </thead>
          <tbody>
            {points.map((p) => (
              <tr key={p.at} className={p.outside ? 'outside' : ''}>
                <td>{dateTime(p.at)}</td>
                <td>
                  {value(p.value)}
                  {p.outside ? ' ⚑' : ''}
                </td>
                <td>
                  {!included
                    ? 'Ikke valgt til analyse'
                    : p.outside === null
                    ? 'Utilstrækkeligt grundlag'
                    : `${value(p.low)}–${value(p.high)}`}
                </td>
                <td>
                  {stateLabels[p.assessment.operatingState]}
                  <small>
                    {p.assessment.contextSource === 'pump'
                      ? 'Pumpetid'
                      : p.assessment.contextSource === 'schedule'
                      ? 'Driftsplan (forventet)'
                      : 'Intet driftssignal'}
                  </small>
                </td>
                <td>
                  {included ? baselineLabels[p.assessment.baselineSource] : 'Ignoreret'}
                  {included && (
                    <small>
                      {p.assessment.historyCount} tidligere perioder
                      {p.assessment.fallback ? ' · standard som fallback' : ''}
                    </small>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!points.length && <p>Ingen målinger matcher filtret.</p>}
      </div>
    </section>
  );
}
